import React, { useState } from 'react'
import { Tooltip } from 'primereact/tooltip'
import { Dropdown } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'
import { Panel } from 'primereact/panel'
import { Tag } from 'primereact/tag'
import { Link } from 'react-router-dom'

import { Environment } from '../../../constants/EnvironmentsEnum'
import {
    ChargePoint,
    useApiCustomerGetAllQuery,
    useChargePointDisableOcppMutation,
    useChargePointEnableOcppMutation,
    useChargePointUpdateChargerInformationMutation,
    useExternalServerGetAllQuery,
} from '../../redux/api/web-api'
import { isEnvironment } from '../../utils/currentEnvironment'
import { ChargePointExternalServerWidget } from '../chargepoint-external-server-widget'
import { mapChargerToChargerInformation } from '../../utils/objectMappers'
import useText from '../../hooks/useText'
import { useToast } from '../../uikit/ToastProvider'

interface IComponentProps {
    chargePoint: ChargePoint
}

const ChargePointDetailsSupportInputs = ({ chargePoint }: IComponentProps) => {
    const text = useText()
    const [callbackEnabled, setCallbackEnabled] = useState(!!chargePoint?.additionalInfo?.callbackEnabledApiCustomerId)
    const [chargePointDetails, setChargePointDetails] = useState(chargePoint)
    const toast = useToast()

    const { data: apiCustomers } = useApiCustomerGetAllQuery(
        {
            enabled: true,
            callbackEnabled: true,
        },
        { refetchOnFocus: true }
    )
    const [chargePointEnableOcpp, { isLoading: enableLoading }] = useChargePointEnableOcppMutation()
    const [chargePointDisableOcpp, { isLoading: disableLoading }] = useChargePointDisableOcppMutation()
    const [updateChargePoint] = useChargePointUpdateChargerInformationMutation()
    const { data: externalServersOptions } = useExternalServerGetAllQuery(undefined, {
        refetchOnFocus: true,
    })

    const toggleOcppAvailability = async (forceUpdate: boolean = false) => {
        try {
            if (chargePoint?.id) {
                if (chargePoint?.ocppEnabled) {
                    await chargePointDisableOcpp({
                        chargePointId: chargePoint?.id,
                        ...(forceUpdate && { forceUpdate }),
                    })
                        .unwrap()
                        .then(() => {
                            toast.notifySuccess(text('lblRelayDisabled'))
                        })
                } else {
                    await chargePointEnableOcpp({
                        chargePointId: chargePoint?.id,
                        ...(forceUpdate && { forceUpdate }),
                    })
                        .unwrap()
                        .then(() => {
                            toast.notifySuccess(text('lblRelayEnabled'))
                        })
                }
            }
        } catch (error) {
            toast.notifyError(text('lblProxyDisableEnableError'))
        }
    }

    const eapiDefault = apiCustomers?.find((customer) => customer.name == 'Easypark')
    const callbackEnabledApiCustomerIdExists = chargePointDetails?.additionalInfo?.callbackEnabledApiCustomerId
        ? chargePointDetails?.additionalInfo?.callbackEnabledApiCustomerId
        : eapiDefault?.id

    const updateExternalServer = async (option?: number | null) => {
        await updateChargePoint({
            chargerInformation: { ...mapChargerToChargerInformation(chargePoint), externalServerId: option },
            chargePointId: chargePoint.id,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgChargerUpdated'))
            })
            .catch(() => {
                toast.notifyError(text('msgChargePointNotUpdated'))
            })
    }

    const updateEapi = async () => {
        await updateChargePoint({
            chargerInformation: {
                ...mapChargerToChargerInformation(chargePoint),
                callbackEnabledApiCustomerId: !callbackEnabled ? callbackEnabledApiCustomerIdExists : null,
            },
            chargePointId: chargePoint.id,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgChargerUpdated'))
            })
            .catch(() => {
                toast.notifyError(text('msgChargePointNotUpdated'))
            })
    }

    const apiCustomerOption = apiCustomers?.map((customer) => ({
        value: customer?.id,
        label: `[${customer.id}] ${customer.name}`,
    }))

    const disableCpms = chargePoint?.ocppEnabled || isEnvironment(Environment?.Production)
    const disabledToggleCpms =
        chargePoint?.isCharging ||
        (!chargePoint?.additionalInfo?.externalServerId && !chargePoint?.ocppEnabled) ||
        isEnvironment(Environment?.Production)
    return (
        <div className="flex flex-row w-full gap-4">
            <Panel
                header={
                    <>
                        {text('lblEapiCustomersWidget')}
                        <Tag
                            className="ml-2"
                            severity={callbackEnabled ? 'success' : null}
                            value={callbackEnabled ? text('lblEnabledStatus') : text('lblDisabledStatus')}
                        ></Tag>
                    </>
                }
                className="w-6"
                data-testid="eapiCustomer-heading"
                icons={
                    <div className="flex">
                        <Tooltip
                            target=".custom-target-icon"
                            content={text('lblEapiExplanationText')}
                            className="w-4"
                            position="bottom"
                        />

                        <i
                            className="custom-target-icon pi pi-info-circle pointer mr-2"
                            style={{ color: 'var(--text-color-info)' }}
                            data-testid="eapi-info-text"
                        ></i>
                    </div>
                }
            >
                <span>
                    <Dropdown
                        className="w-10 mt-4 ml-2"
                        data-testid={
                            chargePointDetails?.additionalInfo?.callbackEnabledApiCustomerId
                                ? `callBackCustomer-select-${chargePointDetails?.additionalInfo?.callbackEnabledApiCustomerId}`
                                : 'callBackCustomer-select'
                        }
                        id="callbackEnabledApiCustomerId"
                        options={apiCustomerOption}
                        value={
                            chargePointDetails?.additionalInfo?.callbackEnabledApiCustomerId
                                ? chargePointDetails?.additionalInfo?.callbackEnabledApiCustomerId
                                : eapiDefault?.id
                        }
                        disabled={callbackEnabled}
                        filter
                        virtualScrollerOptions={{ itemSize: 38 }}
                        onChange={(option) => {
                            setChargePointDetails({
                                ...chargePointDetails,
                                additionalInfo: {
                                    ...chargePointDetails.additionalInfo,
                                    callbackEnabledApiCustomerId: option.target.value
                                        ? option.target.value
                                        : eapiDefault?.id,
                                },
                            })
                        }}
                    />
                </span>

                <InputSwitch
                    style={{ float: 'right' }}
                    checked={callbackEnabled}
                    onChange={() => {
                        updateEapi(), setCallbackEnabled(!callbackEnabled)
                    }}
                    data-testid="eapi-toggle"
                    className="mr-2 mt-5"
                />
            </Panel>

            <Panel
                data-testid="relay-heading"
                header={
                    <>
                        {text('lblRelayWidget')}
                        <Tag
                            className="ml-2"
                            severity={chargePoint?.ocppEnabled ? 'success' : null}
                            value={chargePoint?.ocppEnabled ? text('lblEnabledStatus') : text('lblDisabledStatus')}
                        ></Tag>
                    </>
                }
                className="w-6"
                icons={
                    <div className="flex">
                        <Tooltip
                            target=".custom-target-icon1"
                            content={text('lblProxyRelayInfoText')}
                            className="w-4"
                            position="bottom"
                        />

                        <i
                            className="custom-target-icon1 pi pi-info-circle pointer mr-2"
                            style={{ color: 'var(--text-color-info)' }}
                            data-testid="ceaas-info-text"
                        ></i>

                        <Link to="/externalservers" data-testid="external-server-redirect" title={text('lblGoToProxy')}>
                            <i className="pi pi-external-link" />
                        </Link>
                    </div>
                }
            >
                <ChargePointExternalServerWidget
                    externalServersOptions={externalServersOptions}
                    enableLoading={enableLoading}
                    disableLoading={disableLoading}
                    disabledToggleCpms={disabledToggleCpms}
                    chargePoint={chargePoint}
                    disableCpms={disableCpms}
                    updateExternalServer={updateExternalServer}
                    toggleOcppAvailability={toggleOcppAvailability}
                    disabled={callbackEnabled}
                    placeholder={text('lblSelectOrganisation')}
                />
            </Panel>
        </div>
    )
}

export default ChargePointDetailsSupportInputs
